import React, { useState, useEffect } from 'react'
import { Paragraph, Separator, Row, DialogOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import { appPath, targetEnv } from '@fs/zion-config'
import logo from '../img/fs_logo_favicon_sq.png'
import Urls from '../constants/Urls'

/**
 * Format time in MM:SS format
 * @param {number} timeInSeconds - Time in seconds
 * @returns {string} Formatted time string
 */
const formatTime = (timeInSeconds) => {
  const minutes = Math.floor(timeInSeconds / 60)
  const seconds = timeInSeconds % 60
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

/**
 * Generate a random 6-digit number
 * @returns {string} 6-digit number as string, padded with leading zeros if necessary
 */
const generateVerificationCode = () => {
  return Math.floor(100000 + Math.random() * 900000).toString()
}

/**
 * QR code dialog component for sign-in
 */
const QrDialog = ({ overlay }) => {
  const { t } = useTranslation()

  const baseUrl = targetEnv === 'local' ? 'https://identint.familysearch.org/en/identity/login' : appPath

  const qrCodeUrl = `${baseUrl}${Urls.USE_MY_PHONE}`

  // Initial timer value: 5 minutes (300 seconds)
  const [timeLeft, setTimeLeft] = useState(300)

  // Generate a random 6-digit verification code
  const [verificationCode, setVerificationCode] = useState(() => generateVerificationCode())

  useEffect(() => {
    if (!overlay.isOpen) return undefined

    // Start countdown timer when dialog opens
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer)
          // Generate a new verification code when timer expires
          setVerificationCode(generateVerificationCode())
          return 300 // Reset to 5 minutes
        }
        return prevTime - 1
      })
    }, 1000)

    // Clean up timer when dialog closes or component unmounts
    return () => clearInterval(timer)
  }, [overlay.isOpen])

  // Reset timer and verification code when dialog opens
  useEffect(() => {
    if (overlay.isOpen) {
      setTimeLeft(300)
      setVerificationCode(generateVerificationCode())
    }
  }, [overlay.isOpen])

  return (
    <DialogOverlay {...overlay} size="lg" title={t('qr-page.title', 'FamilySearch Sign-in')}>
      <Separator size="lg" />
      <Row alignX="center">
        <QRCodeSVG
          value={qrCodeUrl}
          size={256}
          bgColor="#ffffff"
          fgColor="#000000"
          level="L"
          aria-label="QR code to sign in to FamilySearch"
          imageSettings={{
            src: logo,
            height: 36,
            width: 36,
            opacity: 1,
            excavate: true,
          }}
        />
      </Row>
      <Separator size="lg" />
      <Paragraph centered>
        {t('qr-page.time-remaining', 'Time remaining')}: {formatTime(timeLeft)}
      </Paragraph>
      <Paragraph centered size="xl">
        {t('qr-page.verification-code', 'Verification Code')}: {verificationCode}
      </Paragraph>
      <Separator size="sm" />
      <Paragraph size="sm">
        {t('qr-page.instructions', '1. Scan this QR code with your phone camera and sign in.')}
      </Paragraph>
      <Paragraph size="sm">
        {t(
          'qr-page.verification-instructions',
          '2. Verify that the 6-digit number shown above matches the one on your phone.'
        )}
      </Paragraph>
      <Separator size="sm" />
    </DialogOverlay>
  )
}

export default QrDialog
